import { useState, useEffect } from "react";
import API, { fetchSymbolsOnboarding } from "../services/api";
import {
  Link as LinkIcon,
  Server,
  User,
  Lock,
  CircleCheck,
  AlertCircle,
  Trash2,
  Building2,
  AlertTriangle,
  Cable,
  FolderKanban,
  ShieldCheck,
  Eye,
  EyeOff,
  RefreshCcw,
  Unplug,
} from "lucide-react";
import CustomSelect from "../components/CustomSelect";
import PageModal from "../components/PageModal";
import RadioButtonGroup from "../components/RadioButtonGroup";
import { useToast } from "../components/Toast";
import { formatAppDateTime } from "../utils/dateFormat";
import {
  buildLinkSymbolOptions,
  enforceDistinctSameBrokerSymbols,
  isDuplicateLinkSymbol,
} from "./linkAccountSymbols";
import { getLinkAccountRequestTone } from "./linkAccountRequestTone";
import "./LinkAccount.css";

const DEFAULT_REVIEW_REASON = "Please prepare terminals and collector config";
const MIRRORED_BROKER_FIELDS = [
  "terminal",
  "brokerName",
  "serverNameInput",
  "accountNumber",
  "password",
];

function getSetId(set) {
  return set?._id || set?.id || "";
}

function getLinkStatus(set) {
  if (!set) return "";
  if (typeof set.linkStatus === "string" && set.linkStatus.trim()) {
    return set.linkStatus;
  }
  return set?.symbolsLocked || set?.futureSymbol || set?.spotSymbol ? "active" : "draft";
}

function normalizeMirroredBrokerValue(value, field) {
  const raw = String(value || "");
  return field === "terminal" ? raw.trim().toUpperCase() : raw.trim();
}

function normalizeBrokerForCompare(broker = {}, index = 0) {
  return {
    position: index + 1,
    brokerName: (broker.brokerName || "").trim(),
    terminal: broker.terminal || "",
    server: (broker.server || broker.serverNameInput || "").trim(),
    accountNumber: (broker.accountNumber || "").trim(),
    password: broker.password || "",
    requestedSymbol: (broker.requestedSymbol || "").trim(),
  };
}

function buildComparableSet({ name = "", brokers = [] } = {}) {
  return {
    name: name.trim(),
    brokers: [0, 1].map((index) => normalizeBrokerForCompare(brokers[index], index)),
  };
}

function inferArbitrageType(brokers = []) {
  if (!Array.isArray(brokers) || brokers.length < 2) return "different_broker";
  const leftBroker = brokers[0] || {};
  const rightBroker = brokers[1] || {};
  const hasAnyMirroredValue = MIRRORED_BROKER_FIELDS.some(
    (field) =>
      normalizeMirroredBrokerValue(leftBroker[field], field) ||
      normalizeMirroredBrokerValue(rightBroker[field], field),
  );

  if (!hasAnyMirroredValue) return "different_broker";

  const allFieldsMatch = MIRRORED_BROKER_FIELDS.every(
    (field) =>
      normalizeMirroredBrokerValue(leftBroker[field], field) ===
      normalizeMirroredBrokerValue(rightBroker[field], field),
  );

  return allFieldsMatch ? "same_broker" : "different_broker";
}

function hasCompleteBrokerDetails(broker) {
  return Boolean(
    broker.brokerName &&
    broker.terminal &&
    broker.server &&
    broker.accountNumber &&
    broker.password,
  );
}

function getLinkStatusMeta(linkStatus) {
  switch (linkStatus) {
    case "draft":
      return {
        label: "Saved",
        helper:
          "Saved locally. Submit this set for review when the broker details and requested symbols are ready.",
        badgeClass: "la-badge-saved",
        listBadgeClass: "la-set-badge-saved",
        itemClass: "la-set-item-saved",
      };
    case "pending_review":
      return {
        label: "Pending Review",
        helper:
          "Submitted for review. Admin approval is still pending, so the saved broker configuration is read-only for now.",
        badgeClass: "la-badge-pending-review",
        listBadgeClass: "la-set-badge-pending-review",
        itemClass: "la-set-item-pending-review",
      };
    case "reconnect_required":
      return {
        label: "Reconnect Required",
        helper:
          "Approved changes are already saved. Run Link & Connect on this approved configuration before the set becomes live again.",
        badgeClass: "la-badge-reconnect-required",
        listBadgeClass: "la-set-badge-reconnect-required",
        itemClass: "la-set-item-reconnect-required",
      };
    case "active":
    default:
      return {
        label: "Active",
        helper:
          "Latest approved configuration is live. Link & Connect is available whenever you need to refresh both broker sessions.",
        badgeClass: "la-badge-active",
        listBadgeClass: "la-set-badge-active",
        itemClass: "la-set-item-active",
      };
  }
}

function getRequestTypeLabel(type) {
  switch (type) {
    case "delete":
      return "Deletion";
    case "link":
      return "Review Submission";
    case "modify":
    default:
      return "Modification";
  }
}

export default function LinkAccount() {
  const toast = useToast();

  const emptyBroker = {
    brokerName: "",
    terminal: "MT4",
    serverNameInput: "",
    accountNumber: "",
    password: "",
    status: "",
    token: null,
    error: null,
    requestedSymbol: "",
    symbols: [],
    symbolsLoading: false,
    symbolMode: "auto",
  };

  const [sets, setSets] = useState([]);
  const [selectedSetId, setSelectedSetId] = useState("new");
  const [name, setName] = useState("");
  const [arbitrageType, setArbitrageType] = useState("different_broker");
  const [brokers, setBrokers] = useState([{ ...emptyBroker, terminal: "MT5" }, { ...emptyBroker }]);
  const [connecting, setConnecting] = useState(false);
  const [role, setRole] = useState("");
  const [requests, setRequests] = useState([]);
  const [requestReason, setRequestReason] = useState("");
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestAction, setRequestAction] = useState(null);
  const [reviewSubmitting, setReviewSubmitting] = useState(false);
  const [renameSubmitting, setRenameSubmitting] = useState(false);
  const [disconnectSubmitting, setDisconnectSubmitting] = useState(false);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ open: false, mode: null });
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const broker1RequestedSymbol = brokers[0]?.requestedSymbol || "";
  const broker2RequestedSymbol = brokers[1]?.requestedSymbol || "";

  useEffect(() => {
    fetchSets();
    fetchRole();
  }, []);

  useEffect(() => {
    if (selectedSetId && selectedSetId !== "new") {
      fetchRequests(selectedSetId);
    } else {
      setRequests([]);
    }
  }, [selectedSetId]);

  useEffect(() => {
    if (arbitrageType !== "same_broker") return;

    setBrokers((current) => {
      const next = enforceDistinctSameBrokerSymbols(current);
      const changed = next.some(
        (broker, index) =>
          (broker?.requestedSymbol || "") !== (current[index]?.requestedSymbol || ""),
      );
      return changed ? next : current;
    });
  }, [arbitrageType, broker1RequestedSymbol, broker2RequestedSymbol]);

  async function fetchSets() {
    try {
      const res = await API.get("/account-sets");
      const data = res.data.data || res.data;
      const nextSets = Array.isArray(data) ? data : [];
      setSets(nextSets);
      return nextSets;
    } catch {
      setSets([]);
      return [];
    }
  }

  async function fetchRole() {
    try {
      const res = await API.get("/users/me");
      setRole(res?.data?.role || "");
    } catch {
      setRole("");
    }
  }

  async function fetchRequests(accountSetId) {
    try {
      const res = await API.get("/account-set-requests", { params: { accountSetId } });
      const data = res.data?.data || [];
      setRequests(Array.isArray(data) ? data : []);
    } catch {
      setRequests([]);
    }
  }

  const isAdmin = role === "admin";
  const currentSet =
    selectedSetId !== "new" ? sets.find((set) => getSetId(set) === selectedSetId) || null : null;
  const currentLinkStatus = currentSet ? getLinkStatus(currentSet) : "";
  const currentLinkStatusMeta = currentSet ? getLinkStatusMeta(currentLinkStatus) : null;
  const savedComparableSet = currentSet ? buildComparableSet(currentSet) : null;
  const formComparableSet = currentSet ? buildComparableSet({ name, brokers }) : null;
  const hasSavedSetChanges = currentSet
    ? JSON.stringify(savedComparableSet) !== JSON.stringify(formComparableSet)
    : false;
  const hasSavedNameChange = currentSet
    ? savedComparableSet.name !== formComparableSet.name
    : false;
  const hasSavedBrokerChanges = currentSet
    ? JSON.stringify(savedComparableSet.brokers) !== JSON.stringify(formComparableSet.brokers)
    : false;
  const hasOnlySavedNameChange =
    Boolean(currentSet) && hasSavedNameChange && !hasSavedBrokerChanges;
  const hasCompleteSavedBrokers = currentSet
    ? savedComparableSet.brokers.every(hasCompleteBrokerDetails)
    : false;
  const hasPendingRequest = requests.some((req) => req?.status === "pending");
  const canSaveName =
    Boolean(currentSet) && hasOnlySavedNameChange && !hasPendingRequest && !renameSubmitting;
  const riskyFieldsLocked =
    Boolean(currentSet) && (hasPendingRequest || currentLinkStatus === "pending_review");

  function buildRequestDetails() {
    return {
      name: name.trim(),
      brokers: brokers.map((broker, index) => ({
        brokerName: broker.brokerName,
        terminal: broker.terminal,
        server: broker.serverNameInput.trim(),
        accountNumber: broker.accountNumber,
        password: broker.password,
        requestedSymbol: broker.requestedSymbol,
        position: index + 1,
      })),
    };
  }

  function resetForm() {
    setName("");
    setArbitrageType("different_broker");
    setBrokers([{ ...emptyBroker, terminal: "MT5" }, { ...emptyBroker }]);
    setVisiblePasswords({});
  }

  function loadSetIntoForm(setObj) {
    setName(setObj?.name || "");
    const sourceBrokers = Array.isArray(setObj?.brokers) ? setObj.brokers : [];
    const loaded = sourceBrokers.map((broker) => ({
      brokerName: broker.brokerName || "",
      terminal: broker.terminal || "MT4",
      serverNameInput: broker.server || broker.serverNameInput || "",
      accountNumber: broker.accountNumber || "",
      password: broker.password || "",
      status: "",
      token: null,
      error: null,
      requestedSymbol: broker.requestedSymbol || "",
      symbols: [],
      symbolsLoading: false,
      symbolMode: broker.symbolMode || "auto",
    }));
    while (loaded.length < 2) loaded.push({ ...emptyBroker });
    setArbitrageType(inferArbitrageType(loaded));
    setBrokers(loaded.slice(0, 2));
    setVisiblePasswords({});
  }

  function mirrorBrokerDetails(sourceBroker, targetBroker) {
    return {
      ...(targetBroker || { ...emptyBroker }),
      terminal: sourceBroker.terminal,
      brokerName: sourceBroker.brokerName,
      serverNameInput: sourceBroker.serverNameInput,
      accountNumber: sourceBroker.accountNumber,
      password: sourceBroker.password,
      status: "",
      token: null,
      error: null,
      symbols: [...(sourceBroker.symbols || [])],
      symbolsLoading: sourceBroker.symbolsLoading,
    };
  }

  function handleArbitrageTypeChange(nextType) {
    setArbitrageType(nextType);

    if (nextType !== "same_broker") return;

    setBrokers((current) => {
      const next = [...current];
      const sourceBroker = next[0] || { ...emptyBroker, terminal: "MT5" };
      const targetBroker = next[1] || { ...emptyBroker };
      next[1] = mirrorBrokerDetails(sourceBroker, targetBroker);
      if (isDuplicateLinkSymbol(next[0]?.requestedSymbol, next[1]?.requestedSymbol)) {
        next[1] = { ...next[1], requestedSymbol: "" };
      }
      return next;
    });
  }

  async function submitModifyRequest() {
    if (selectedSetId === "new") return;
    if (!validateForm() || !name.trim()) {
      toast.warning("Please fill all broker fields and set name before requesting changes.");
      return;
    }
    if (hasPendingRequest) {
      toast.warning("This account set already has a pending request.");
      return;
    }
    if (!["active", "reconnect_required"].includes(currentLinkStatus)) {
      toast.warning(
        "Modification requests are available only after the account set has been reviewed.",
      );
      return;
    }
    if (!hasSavedBrokerChanges) {
      if (hasOnlySavedNameChange) {
        toast.warning(
          "Set name can be saved directly. Use Save Name instead of a modification request.",
        );
      } else {
        toast.warning(
          "Change a broker, credential, account, server, password, or symbol before requesting a modification.",
        );
      }
      return;
    }
    if (!hasSavedSetChanges) {
      toast.warning("Change a saved field before requesting a modification.");
      return;
    }
    setRequestAction("modify");
    setRequestSubmitting(true);
    try {
      await API.post("/account-set-requests", {
        accountSetId: selectedSetId,
        type: "modify",
        reason: requestReason,
        details: buildRequestDetails(),
      });
      setRequestReason("");
      toast.success("Modification request submitted. Pending admin review.");
      await fetchRequests(selectedSetId);
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setRequestSubmitting(false);
      setRequestAction(null);
    }
  }

  function openConfirmModal(mode) {
    setConfirmModal({ open: true, mode });
  }

  function closeConfirmModal() {
    setConfirmModal({ open: false, mode: null });
  }

  async function submitDeleteRequest() {
    if (selectedSetId === "new") return;
    if (hasPendingRequest) {
      toast.warning("This account set already has a pending request.");
      return;
    }
    setRequestAction("delete");
    setRequestSubmitting(true);
    try {
      await API.post("/account-set-requests", {
        accountSetId: selectedSetId,
        type: "delete",
        reason: requestReason,
      });
      setRequestReason("");
      toast.success("Deletion request submitted. Pending admin review.");
      await fetchRequests(selectedSetId);
      closeConfirmModal();
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setRequestSubmitting(false);
      setRequestAction(null);
    }
  }

  function handleSetChange(id) {
    setSelectedSetId(id);
    setRequestReason("");
    if (id === "new") {
      resetForm();
    } else {
      const setObj = sets.find((set) => getSetId(set) === id);
      if (setObj) {
        loadSetIntoForm(setObj);
      } else {
        resetForm();
      }
    }
  }

  function handleFieldChange(idx, field, val) {
    if (field === "accountNumber") val = val.replace(/\D/g, "");

    setBrokers((current) => {
      const next = [...current];
      next[idx] = { ...next[idx], [field]: val, status: "", error: null, token: null, symbols: [] };

      if (arbitrageType === "same_broker" && idx === 0 && MIRRORED_BROKER_FIELDS.includes(field)) {
        next[1] = mirrorBrokerDetails(next[0], next[1]);
      }

      return next;
    });
  }

  async function fetchBrokerSymbols(idx) {
    const broker = brokers[idx];
    const shouldMirrorSymbols = arbitrageType === "same_broker" && idx === 0;

    setBrokers((current) => {
      const next = [...current];
      next[idx] = { ...next[idx], symbolsLoading: true };
      if (shouldMirrorSymbols) {
        next[1] = { ...next[1], symbolsLoading: true };
      }
      return next;
    });

    try {
      const res = await fetchSymbolsOnboarding({
        terminal: broker.terminal,
        brokerName: broker.brokerName,
        serverName: broker.serverNameInput.trim(),
        accountNumber: broker.accountNumber,
        password: broker.password,
      });
      if (res.data?.success && Array.isArray(res.data.symbols)) {
        const symbols = res.data.symbols.map((s) =>
          typeof s === "string" ? s : s.currency || s.name || s.symbol || String(s),
        );
        setBrokers((curr) => {
          const next = [...curr];
          next[idx] = { ...next[idx], symbols, symbolsLoading: false };
          if (shouldMirrorSymbols) {
            next[1] = { ...next[1], symbols: [...symbols], symbolsLoading: false };
          }
          return next;
        });
      } else {
        toast.error(res.data?.message || "Could not fetch symbols.");
        setBrokers((curr) => {
          const next = [...curr];
          next[idx] = { ...next[idx], symbolsLoading: false };
          if (shouldMirrorSymbols) {
            next[1] = { ...next[1], symbolsLoading: false };
          }
          return next;
        });
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not fetch symbols.");
      setBrokers((curr) => {
        const next = [...curr];
        next[idx] = { ...next[idx], symbolsLoading: false };
        if (shouldMirrorSymbols) {
          next[1] = { ...next[1], symbolsLoading: false };
        }
        return next;
      });
    }
  }

  function handleSymbolModeChange(idx, mode) {
    setBrokers((curr) => {
      const next = [...curr];
      next[idx] = { ...next[idx], symbolMode: mode };
      return next;
    });
  }

  function handleSymbolChange(idx, val) {
    setBrokers((curr) => {
      const otherIdx = idx === 0 ? 1 : 0;
      const otherSymbol = curr[otherIdx]?.requestedSymbol || "";
      if (arbitrageType === "same_broker" && isDuplicateLinkSymbol(val, otherSymbol)) {
        toast.warning("Broker 1 and Broker 2 cannot use the same symbol for same-broker trades.");
        return curr;
      }

      const next = [...curr];
      next[idx] = { ...next[idx], requestedSymbol: val };
      return next;
    });
  }

  function togglePasswordVisibility(idx) {
    setVisiblePasswords((current) => ({
      ...current,
      [idx]: !current[idx],
    }));
  }

  function validateForm() {
    if (
      arbitrageType === "same_broker" &&
      isDuplicateLinkSymbol(brokers[0]?.requestedSymbol, brokers[1]?.requestedSymbol)
    ) {
      return false;
    }

    return brokers.every(
      (b) =>
        b.brokerName.trim() !== "" &&
        b.terminal !== "" &&
        b.serverNameInput.trim() !== "" &&
        b.accountNumber.trim() !== "" &&
        b.password.trim() !== "",
    );
  }

  async function persistAccountSet() {
    const payload = {
      name: name.trim(),
      brokers: brokers.map((b) => ({
        brokerName: b.brokerName,
        terminal: b.terminal,
        server: b.serverNameInput.trim(),
        accountNumber: b.accountNumber,
        password: b.password,
        ...(b.requestedSymbol ? { requestedSymbol: b.requestedSymbol } : {}),
      })),
    };
    const response =
      selectedSetId === "new"
        ? await API.post("/account-sets", payload)
        : await API.patch(`/account-sets/${selectedSetId}`, payload);
    const savedSet = response?.data?.data || response?.data || null;
    const savedSetId = getSetId(savedSet) || selectedSetId;
    const nextSets = await fetchSets();
    const matchedSet = nextSets.find((set) => getSetId(set) === savedSetId) || savedSet;

    if (savedSetId && savedSetId !== "new") {
      setSelectedSetId(savedSetId);
      if (matchedSet) loadSetIntoForm(matchedSet);
    } else {
      setSelectedSetId("new");
      resetForm();
    }

    return {
      savedSetId,
      matchedSet,
    };
  }

  async function handleSaveName() {
    if (!currentSet || !canSaveName) return;

    setRenameSubmitting(true);
    try {
      await API.patch(`/account-sets/${selectedSetId}`, {
        name: name.trim(),
      });
      const nextSets = await fetchSets();
      const matchedSet = nextSets.find((set) => getSetId(set) === selectedSetId);
      if (matchedSet) {
        loadSetIntoForm(matchedSet);
      }
      toast.success("Set name saved.");
    } catch (err) {
      toast.error(err.response?.data?.error || err.message);
    } finally {
      setRenameSubmitting(false);
    }
  }

  async function handleLinkAndConnect() {
    if (!currentSet) return;
    if (!hasCompleteSavedBrokers) {
      toast.warning("Save a complete account set before connecting it.");
      return;
    }
    if (hasSavedSetChanges) {
      toast.warning(
        isAdmin
          ? "Save the latest changes before linking and connecting."
          : "You have unsaved local edits. Revert them or request a modification before linking and connecting.",
      );
      return;
    }

    setConnecting(true);
    setBrokers((curr) =>
      curr.map((broker) => ({
        ...broker,
        status: "connecting",
        error: null,
        token: null,
      })),
    );

    try {
      const response = await API.post(`/account-sets/${selectedSetId}/connect`);
      const connectedPositions = Array.isArray(response.data?.connectedPositions)
        ? response.data.connectedPositions.map((position) => Number(position))
        : [1, 2];

      setBrokers((curr) =>
        curr.map((broker, idx) => ({
          ...broker,
          status: connectedPositions.includes(idx + 1) ? "connected" : "",
          error: null,
        })),
      );

      await fetchSets();
      toast.success(response.data?.message || "Accounts connected successfully.");
    } catch (err) {
      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        err.message ||
        "Connection failed";
      const connectedPositions = Array.isArray(err.response?.data?.connectedPositions)
        ? err.response.data.connectedPositions.map((position) => Number(position))
        : [];
      const failedPosition = Number(err.response?.data?.brokerPosition);

      setBrokers((curr) =>
        curr.map((broker, idx) => {
          const position = idx + 1;
          if (connectedPositions.includes(position)) {
            return { ...broker, status: "connected", error: null };
          }
          if (!failedPosition || failedPosition === position) {
            return { ...broker, status: "error", error: errorMessage };
          }
          return { ...broker, status: "", error: null };
        }),
      );

      toast.error(errorMessage);
    } finally {
      setConnecting(false);
    }
  }

  async function handleSubmitForReview() {
    let targetSetId = selectedSetId;

    if (selectedSetId === "new") {
      if (!validateForm() || !name.trim()) {
        toast.warning("Please fill all broker fields and set name before submitting for review.");
        return;
      }
    } else if (!currentSet) {
      return;
    }

    if (selectedSetId !== "new" && currentLinkStatus !== "draft") {
      toast.warning("Only draft account sets can be submitted for review.");
      return;
    }
    if (selectedSetId !== "new" && hasPendingRequest) {
      toast.warning("This account set already has a pending request.");
      return;
    }

    const bothSymbolsSelected = brokers.every((b) => b.requestedSymbol?.trim());
    if (!bothSymbolsSelected) {
      toast.warning(
        "Please select or enter a symbol for both brokers before submitting for review.",
      );
      return;
    }

    if (
      arbitrageType === "same_broker" &&
      isDuplicateLinkSymbol(brokers[0]?.requestedSymbol, brokers[1]?.requestedSymbol)
    ) {
      toast.warning("Broker 1 and Broker 2 must use different symbols for same-broker trades.");
      return;
    }

    setReviewSubmitting(true);
    try {
      const reviewPayload = {
        reason: requestReason.trim() || DEFAULT_REVIEW_REASON,
      };

      if (selectedSetId === "new") {
        const persisted = await persistAccountSet();
        targetSetId = persisted.savedSetId;
      } else if (selectedSetId !== "new" && hasSavedSetChanges) {
        reviewPayload.details = buildRequestDetails();
      }

      await API.post(`/account-sets/${targetSetId}/submit-review`, reviewPayload);
      const nextSets = await fetchSets();
      const matchedSet = nextSets.find((set) => getSetId(set) === targetSetId);
      if (matchedSet) {
        loadSetIntoForm(matchedSet);
      }
      await fetchRequests(targetSetId);
      setRequestReason("");
      toast.success("Submitted for admin review");
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setReviewSubmitting(false);
    }
  }

  async function handleDeleteSet() {
    if (!isAdmin) {
      openConfirmModal("request-delete");
      return;
    }
    setDeleteSubmitting(true);
    try {
      await API.delete(`/account-sets/${selectedSetId}`);
      await fetchSets();
      setSelectedSetId("new");
      resetForm();
      toast.success("Account set deleted.");
      closeConfirmModal();
    } catch (err) {
      toast.error("Could not delete set: " + err.message);
    } finally {
      setDeleteSubmitting(false);
    }
  }

  async function handleDisconnectSet() {
    if (selectedSetId === "new" || !currentSet) return;

    setDisconnectSubmitting(true);
    try {
      const response = await API.patch(`/account-sets/${selectedSetId}/unlock-symbols`);
      const nextSets = await fetchSets();
      const matchedSet =
        nextSets.find((set) => getSetId(set) === selectedSetId) || response?.data?.data || null;
      if (matchedSet) {
        loadSetIntoForm(matchedSet);
      }
      toast.success(response?.data?.message || "Account set disconnected.");
      closeConfirmModal();
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || err.message);
    } finally {
      setDisconnectSubmitting(false);
    }
  }

  const confirmModalConfig =
    confirmModal.mode === "request-delete"
      ? {
          title: "Request deletion for this account set?",
          description:
            "This sends a deletion request for admin review. The account set will remain available until the request is approved.",
          confirmLabel: requestSubmitting ? "Submitting..." : "Request deletion",
          onConfirm: submitDeleteRequest,
          loading: requestSubmitting,
        }
      : confirmModal.mode === "disconnect-set"
        ? {
            title: "Disconnect this account set?",
            description:
              "This stops the live symbol lock and premium tracking for this set. Broker details, requests, closed trades, pending trades, and historical premium data stay saved.",
            confirmLabel: disconnectSubmitting ? "Disconnecting..." : "Disconnect",
            onConfirm: handleDisconnectSet,
            loading: disconnectSubmitting,
          }
      : {
          title: "Delete this account set?",
          description:
            "This permanently removes the selected account set and its configuration. This action cannot be undone.",
          confirmLabel: deleteSubmitting ? "Deleting..." : "Delete set",
          onConfirm: handleDeleteSet,
          loading: deleteSubmitting,
        };

  const getStatusIcon = (status) => {
    switch (status) {
      case "connected":
        return <CircleCheck />;
      case "error":
        return <AlertCircle />;
      case "connecting":
        return <div className="la-spinner" />;
      default:
        return null;
    }
  };

  const showRequestSection = Boolean(currentSet) && currentLinkStatus !== "draft";
  const showSubmitForReview =
    selectedSetId === "new" || (Boolean(currentSet) && currentLinkStatus === "draft");
  const showPendingReviewAction = Boolean(currentSet) && currentLinkStatus === "pending_review";
  const showLinkAndConnect =
    Boolean(currentSet) && ["active", "reconnect_required"].includes(currentLinkStatus);
  const showDisconnect =
    Boolean(currentSet) &&
    currentLinkStatus === "active" &&
    (currentSet?.symbolsLocked ||
      currentSet?.futureSymbol ||
      currentSet?.spotSymbol ||
      currentSet?.premiumTableName);
  const hasRequestedSymbolsSelected = brokers.every((b) => b.requestedSymbol?.trim());
  const submitForReviewDisabled =
    reviewSubmitting ||
    hasPendingRequest ||
    !hasRequestedSymbolsSelected ||
    (selectedSetId === "new"
      ? !validateForm() || !name.trim()
      : currentLinkStatus !== "draft" || !validateForm() || !name.trim());
  const submitForReviewTitle =
    selectedSetId === "new"
      ? "Submit this account set for admin review."
      : hasPendingRequest
        ? "This account set already has a pending request."
        : "Submit this draft account set for admin review.";
  const currentStatusHelperText = currentLinkStatusMeta?.helper || "";
  const isPositiveLinkStatus = ["draft", "active"].includes(currentLinkStatus);

  const formatRequestTime = (timestamp) => {
    if (!timestamp) return "";
    return formatAppDateTime(timestamp, "");
  };
  const setCountLabel = `${sets.length} ${sets.length === 1 ? "Set" : "Sets"}`;

  return (
    <>
      <div className="la-page">
        <div className="la-header">
          <h1>
            <Cable className="la-header-icon" />
            Link Broker Accounts
          </h1>
          <p>Set up your trading account connections</p>
        </div>
        <div className="la-stack">
          <div className="la-card">
            <div className="la-card-heading">
              <div className="la-card-heading-copy">
                <div className="la-card-title">
                  <FolderKanban />
                  <span>Set Configuration</span>
                </div>
                <p>Create or select an account set to manage.</p>
              </div>
            </div>
            <div className="la-config-row">
              <div className="la-field-group la-config-field-compact">
                <label className="la-label">Set Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  disabled={selectedSetId !== "new" && (hasPendingRequest || renameSubmitting)}
                  placeholder="Enter a name for this set"
                  className="la-input"
                />
              </div>
              <div className="la-field-group la-config-field-compact">
                <label className="la-label">Account Set</label>
                <CustomSelect
                  value={selectedSetId}
                  onChange={handleSetChange}
                  options={[
                    { value: "new", label: "+ New Set" },
                    ...sets.map((set) => ({ value: getSetId(set), label: set.name })),
                  ]}
                />
              </div>
              <div className="la-field-group la-arbitrage-wrap">
                <label className="la-label">Arbitrage Type</label>
                <RadioButtonGroup
                  name="link-account-arbitrage-type"
                  value={arbitrageType}
                  onChange={handleArbitrageTypeChange}
                  disabled={riskyFieldsLocked}
                  options={[
                    { value: "different_broker", label: "Different Broker" },
                    { value: "same_broker", label: "Same Broker" },
                  ]}
                />
              </div>
              <div className="la-config-actions">
                {canSaveName && (
                  <button
                    onClick={handleSaveName}
                    disabled={!canSaveName}
                    className="la-btn la-btn-ghost la-btn-save"
                  >
                    {renameSubmitting ? <div className="la-spinner" /> : <CircleCheck />}
                    {renameSubmitting ? "Saving..." : "Save Name"}
                  </button>
                )}
                {showSubmitForReview && (
                  <button
                    onClick={handleSubmitForReview}
                    disabled={submitForReviewDisabled}
                    title={submitForReviewTitle}
                    className="la-btn la-btn-ghost la-btn-save"
                  >
                    {reviewSubmitting ? <div className="la-spinner" /> : <ShieldCheck />}
                    {reviewSubmitting ? "Submitting..." : "Submit For Review"}
                  </button>
                )}
                {showPendingReviewAction && (
                  <button disabled className="la-btn la-btn-ghost la-btn-save">
                    <ShieldCheck />
                    Pending Review
                  </button>
                )}
                {showLinkAndConnect && (
                  <button
                    onClick={handleLinkAndConnect}
                    disabled={connecting || !hasCompleteSavedBrokers || hasSavedSetChanges}
                    className="la-btn la-btn-ghost la-btn-save"
                  >
                    {connecting ? <div className="la-spinner" /> : <Cable />}
                    {connecting ? "Connecting..." : "Link & Connect"}
                  </button>
                )}
                {showDisconnect && (
                  <button
                    onClick={() => openConfirmModal("disconnect-set")}
                    disabled={disconnectSubmitting || connecting}
                    className="la-btn la-btn-warning"
                  >
                    {disconnectSubmitting ? <div className="la-spinner" /> : <Unplug />}
                    {disconnectSubmitting ? "Disconnecting..." : "Disconnect"}
                  </button>
                )}
                {selectedSetId !== "new" && isAdmin && (
                  <button
                    onClick={() => openConfirmModal("delete-set")}
                    className="la-btn la-btn-danger"
                  >
                    <Trash2 />
                    Delete
                  </button>
                )}
              </div>
            </div>

            {currentLinkStatusMeta && currentStatusHelperText && (
              <div
                className={`la-alert la-status-helper-alert ${isPositiveLinkStatus ? "la-alert-success" : "la-alert-warning"}`}
              >
                {isPositiveLinkStatus ? <CircleCheck /> : <AlertTriangle />}
                <span>
                  <strong>{currentLinkStatusMeta.label}.</strong> {currentStatusHelperText}
                </span>
              </div>
            )}

            <div className="la-brokers-grid">
              {brokers.map((broker, idx) => {
                const isMirroredBroker = arbitrageType === "same_broker" && idx === 1;
                const passwordVisibilityKey = isMirroredBroker ? 0 : idx;
                const otherBroker = brokers[idx === 0 ? 1 : 0] || {};
                const symbolOptions = buildLinkSymbolOptions(
                  broker,
                  arbitrageType === "same_broker" ? otherBroker.requestedSymbol : "",
                );

                return (
                  <div key={idx} className="la-broker-section">
                    <div className="la-broker-section-header">
                      <div className="la-card-title">
                        <LinkIcon />
                        Broker {idx + 1}
                      </div>
                      <div
                        className={`la-status${broker.status ? ` la-status-${broker.status}` : ""}`}
                      >
                        {getStatusIcon(broker.status)}
                        {broker.status || "Not Connected"}
                      </div>
                    </div>

                    <div className="la-warning">
                      <AlertTriangle />
                      <span>
                        {isMirroredBroker
                          ? "Broker 2 mirrors Broker 1 details except symbols."
                          : idx === 0
                            ? "Broker 1 must support Futures trading pairs."
                            : "Broker 2  must support Spot trading pairs."}
                      </span>
                    </div>

                    <div className="la-field-group">
                      <label className="la-label">
                        <Server />
                        Terminal
                      </label>
                      <CustomSelect
                        value={broker.terminal}
                        onChange={(val) => handleFieldChange(idx, "terminal", val)}
                        disabled={riskyFieldsLocked || isMirroredBroker}
                        options={[
                          { value: "MT4", label: "MT4" },
                          { value: "MT5", label: "MT5" },
                        ]}
                      />
                    </div>

                    <div className="la-broker-row">
                      <div className="la-field-group">
                        <label className="la-label">
                          <Building2 />
                          Broker Name
                        </label>
                        <input
                          type="text"
                          value={broker.brokerName}
                          onChange={(e) => handleFieldChange(idx, "brokerName", e.target.value)}
                          disabled={riskyFieldsLocked || isMirroredBroker}
                          maxLength={50}
                          placeholder={idx === 0 ? "e.g., VPFX" : "e.g., OctaFX"}
                          className="la-input"
                        />
                      </div>
                      <div className="la-field-group">
                        <label className="la-label">
                          <Server />
                          Server Name
                        </label>
                        <input
                          type="text"
                          value={broker.serverNameInput}
                          onChange={(e) =>
                            handleFieldChange(idx, "serverNameInput", e.target.value)
                          }
                          disabled={riskyFieldsLocked || isMirroredBroker}
                          maxLength={50}
                          placeholder="Enter server name"
                          className="la-input"
                        />
                      </div>
                    </div>

                    <div className="la-broker-row">
                      <div className="la-field-group">
                        <label className="la-label">
                          <User />
                          Account Number
                        </label>
                        <input
                          type="text"
                          inputMode="numeric"
                          pattern="[0-9]*"
                          value={broker.accountNumber}
                          onChange={(e) => handleFieldChange(idx, "accountNumber", e.target.value)}
                          disabled={riskyFieldsLocked || isMirroredBroker}
                          maxLength={50}
                          placeholder="Enter account number"
                          className="la-input"
                        />
                      </div>
                      <div className="la-field-group">
                        <label className="la-label">
                          <Lock />
                          Password
                        </label>
                        <div className="la-password-wrap">
                          <input
                            type={visiblePasswords[passwordVisibilityKey] ? "text" : "password"}
                            value={broker.password}
                            onChange={(e) => handleFieldChange(idx, "password", e.target.value)}
                            disabled={riskyFieldsLocked || isMirroredBroker}
                            maxLength={50}
                            placeholder="Enter trading password"
                            className="la-input la-password-input"
                          />
                          <button
                            type="button"
                            className="la-password-toggle"
                            onClick={() => togglePasswordVisibility(passwordVisibilityKey)}
                            aria-label={
                              visiblePasswords[passwordVisibilityKey]
                                ? "Hide password"
                                : "Show password"
                            }
                            disabled={riskyFieldsLocked}
                          >
                            {visiblePasswords[passwordVisibilityKey] ? (
                              <EyeOff size={18} />
                            ) : (
                              <Eye size={18} />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div className="la-field-group">
                      <label className="la-label">Symbol Entry</label>
                      <RadioButtonGroup
                        name={`symbol-mode-${idx}`}
                        value={broker.symbolMode || "auto"}
                        onChange={(val) => handleSymbolModeChange(idx, val)}
                        disabled={riskyFieldsLocked}
                        options={[
                          { value: "auto", label: "Fetch from Broker" },
                          { value: "manual", label: "Enter Manually" },
                        ]}
                      />
                    </div>

                    {(broker.symbolMode || "auto") === "auto" ? (
                      <div className="la-symbol-row">
                        <button
                          type="button"
                          className="la-btn la-btn-ghost la-btn-fetch-symbols"
                          onClick={() => fetchBrokerSymbols(idx)}
                          disabled={
                            riskyFieldsLocked ||
                            !hasCompleteBrokerDetails({
                              brokerName: broker.brokerName,
                              terminal: broker.terminal,
                              server: broker.serverNameInput,
                              accountNumber: broker.accountNumber,
                              password: broker.password,
                            }) ||
                            broker.symbolsLoading
                          }
                        >
                          {broker.symbolsLoading ? (
                            <div className="la-spinner" />
                          ) : (
                            <RefreshCcw size={15} />
                          )}
                          {broker.symbolsLoading ? "Fetching..." : "Fetch Symbols"}
                        </button>
                        <div className="la-symbol-select-wrap">
                          <CustomSelect
                            value={broker.requestedSymbol}
                            onChange={(val) => handleSymbolChange(idx, val)}
                            options={symbolOptions}
                            placeholder={
                              broker.symbols.length === 0
                                ? broker.requestedSymbol || "No fetched symbols yet"
                                : "Select fetched symbol"
                            }
                            disabled={riskyFieldsLocked || broker.symbols.length === 0}
                          />
                        </div>
                      </div>
                    ) : (
                      <div className="la-symbol-row">
                        <input
                          type="text"
                          value={broker.requestedSymbol}
                          onChange={(e) => handleSymbolChange(idx, e.target.value)}
                          disabled={riskyFieldsLocked}
                          maxLength={50}
                          placeholder={idx === 0 ? "e.g., BTCUSD" : "e.g., XAUUSD"}
                          className="la-input"
                          style={{ flex: 1 }}
                        />
                      </div>
                    )}

                    {broker.error && (
                      <div className="la-field-error">
                        <AlertCircle />
                        {broker.error}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {showRequestSection && (
            <div className="la-card">
              <div className="la-request-section">
                <div className="la-request-header">
                  <div className="la-request-header-text">
                    <div className="la-card-title">
                      <ShieldCheck />
                      <span>Request Account Set Changes</span>
                    </div>
                    <p>
                      {isAdmin
                        ? "Edit the fields above, then submit a modification request for review. Delete remains a direct admin action."
                        : "Edit the fields above, then submit your request for admin approval."}
                    </p>
                    {(currentLinkStatus === "active" ||
                      currentLinkStatus === "reconnect_required") && (
                      <p>
                        Approved risky changes are saved only after admin approval, and the next
                        state will be Reconnect Required until Link &amp; Connect succeeds.
                      </p>
                    )}
                  </div>
                  <span className="la-badge la-badge-review">Admin Review</span>
                </div>

                <label className="la-label">Reason (optional)</label>
                <div className="la-request-grid">
                  <textarea
                    value={requestReason}
                    onChange={(e) => setRequestReason(e.target.value)}
                    disabled={hasPendingRequest}
                    placeholder="Describe the changes you'd like to make or the reason for requesting deletion"
                    className="la-textarea la-request-textarea"
                  />
                  <div className="la-request-actions">
                    <button
                      className="la-btn la-btn-ghost"
                      onClick={submitModifyRequest}
                      disabled={
                        requestSubmitting ||
                        !validateForm() ||
                        !name.trim() ||
                        !hasSavedBrokerChanges ||
                        hasPendingRequest
                      }
                    >
                      {requestSubmitting && requestAction === "modify"
                        ? "Submitting..."
                        : "Request Modification"}
                    </button>
                    {!isAdmin && (
                      <button
                        className="la-btn la-btn-danger"
                        onClick={() => openConfirmModal("request-delete")}
                        disabled={requestSubmitting || hasPendingRequest}
                      >
                        {requestSubmitting && requestAction === "delete"
                          ? "Submitting..."
                          : "Request Deletion"}
                      </button>
                    )}
                  </div>
                </div>

                {hasPendingRequest && (
                  <div className="la-alert la-alert-warning">
                    <AlertTriangle />
                    <span>
                      <strong>Pending Request.</strong> Risky fields, rename, and new request
                      actions stay disabled until the current request is approved or rejected.
                    </span>
                  </div>
                )}

                {requests.length > 0 && (
                  <div className="la-request-history">
                    <div className="la-request-history-header">
                      <h4>Recent Requests</h4>
                      <span>{requests.length} Total</span>
                    </div>
                    <div className="la-request-list">
                      {requests.map((req) => {
                        const requestTone = getLinkAccountRequestTone(req.status);
                        return (
                          <div
                            key={req.id}
                            className={`la-request-item la-request-item-${requestTone}`}
                          >
                            <div className="la-request-item-info">
                              <strong className="la-request-item-title">
                                {getRequestTypeLabel(req.type)}
                              </strong>
                              <div className="la-request-item-meta">
                                <span>{formatRequestTime(req.createdAt)}</span>
                                {req.reason && (
                                  <span className="la-request-reason">Reason: {req.reason}</span>
                                )}
                              </div>
                            </div>
                            <span className={`la-request-status ${req.status}`}>{req.status}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {sets.length > 0 && (
            <div className="la-card">
              <div className="la-card-title-row">
                <div className="la-card-heading-copy">
                  <div className="la-card-title">
                    <LinkIcon />
                    <span>Your Account Sets</span>
                  </div>
                  <p>Review the account sets currently saved to your workspace.</p>
                </div>
                <div className="la-badge la-badge-online">{setCountLabel}</div>
              </div>
              <div className="la-set-table">
                <div className="la-set-thead" aria-hidden="true">
                  <div className="la-set-count-cell">
                    <strong>{sets.length}</strong>
                    <span>{sets.length === 1 ? "set" : "sets"}</span>
                  </div>
                  <span className="la-set-col-label">Name</span>
                  <span className="la-set-col-label">Brokers</span>
                  <span className="la-set-col-label">Status</span>
                </div>
                {sets.map((set) => {
                  const status = getLinkStatus(set);
                  const setStatusMeta = getLinkStatusMeta(status);
                  const statusIcon =
                    status === "active" ? (
                      <CircleCheck size={11} />
                    ) : status === "reconnect_required" ? (
                      <AlertTriangle size={11} />
                    ) : status === "pending_review" ? (
                      <AlertCircle size={11} />
                    ) : null;
                  return (
                    <article
                      key={getSetId(set)}
                      className={`la-set-row ${setStatusMeta.itemClass}`}
                    >
                      <span className="la-set-indicator" />
                      <strong className="la-set-row-name">{set.name}</strong>
                      <div className="la-set-row-brokers">
                        {set.brokers.map((broker, idx) => (
                          <span key={idx} className="la-broker-pill">
                            {broker.terminal && (
                              <span className="la-broker-pill-terminal">{broker.terminal}</span>
                            )}
                            <span className="la-broker-pill-server">{broker.server}</span>
                            {broker.accountNumber && (
                              <span className="la-broker-pill-account">
                                · {broker.accountNumber}
                              </span>
                            )}
                          </span>
                        ))}
                      </div>
                      <span className={`la-set-status ${setStatusMeta.listBadgeClass}`}>
                        {statusIcon}
                        {setStatusMeta.label}
                      </span>
                    </article>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>

      <PageModal
        open={confirmModal.open}
        onClose={closeConfirmModal}
        onConfirm={confirmModalConfig.onConfirm}
        title={confirmModalConfig.title}
        description={confirmModalConfig.description}
        confirmLabel={confirmModalConfig.confirmLabel}
        loading={confirmModalConfig.loading}
        tone="danger"
      />
    </>
  );
}
